/**
 * Modal History Manager with URL Parameters
 * Uses working push/pop mechanism + adds propertyId to URL
 */

// Debounced URL update to prevent excessive re-renders
let urlUpdateTimeout: NodeJS.Timeout | null = null;

export const propertyUrlSync = {
	/**
	 * Close modal manually (X button, etc) - go back in history
	 */
	manualClose: () => {
		if (typeof window === "undefined") return;
		const currentParams = new URLSearchParams(window.location.search);
		if (currentParams.has("propertyId")) {
			// Go back in history - this will naturally remove propertyId
			window.history.back();
		}
	},

	/**
	 * Close modal without navigation (clicking outside, escape key, etc)
	 * Just removes propertyId from current URL without going back
	 */
	closeWithoutNavigation: () => {
		if (typeof window === "undefined") return;

		// Remove propertyId from URL
		const currentParams = new URLSearchParams(window.location.search);
		if (currentParams.has("propertyId")) {
			currentParams.delete("propertyId");
			const newUrl = currentParams.toString()
				? `${window.location.pathname}?${currentParams.toString()}`
				: window.location.pathname;

			// Replace current URL to remove propertyId
			window.history.replaceState(null, "", newUrl);
		}
	},

	/**
	 * Get current propertyId from URL (for sharing/bookmarks)
	 */
	getCurrentPropertyId: () => {
		if (typeof window === "undefined") return null;
		return new URLSearchParams(window.location.search).get("propertyId");
	},

	/**
	 * Update propertyId in URL during carousel navigation
	 */
	updatePropertyId: (propertyId: string) => {
		if (typeof window === "undefined") return;

		// Update URL parameter
		const currentParams = new URLSearchParams(window.location.search);
		currentParams.set("propertyId", propertyId);
		const newUrl = `${window.location.pathname}?${currentParams.toString()}`;

		// Use replaceState to update URL without affecting history stack
		window.history.replaceState({ modalOpen: true }, "", newUrl);
	},

	/**
	 * Update propertyId in URL with debouncing to prevent excessive re-renders
	 */
	// update it only after a minute
	updatePropertyIdDebounced: (propertyId: string, delay: number = 1 * 60 * 1000) => {
		if (typeof window === "undefined") return;

		// Clear any pending update
		if (urlUpdateTimeout) {
			clearTimeout(urlUpdateTimeout);
		}

		// Schedule debounced update
		urlUpdateTimeout = setTimeout(() => {
			const currentParams = new URLSearchParams(window.location.search);
			currentParams.set("propertyId", propertyId);
			const newUrl = `${window.location.pathname}?${currentParams.toString()}`;

			window.history.replaceState({ modalOpen: true }, "", newUrl);
		}, delay);
	},

	/**
	 * Clear any pending debounced URL updates
	 */
	clearPendingUpdates: () => {
		if (urlUpdateTimeout) {
			clearTimeout(urlUpdateTimeout);
			urlUpdateTimeout = null;
		}
	},
};
